import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { Request } from 'commons/utils/request'
import { Hr, Layer, LeaveConfirmation, Page, PageFace, Panel, Toast, handleFormSubmissionError } from 'ui/components'
import { AnnualReviewEditMode } from '~bank-annual-review/types/Manage'

import { Footer } from './components/Footer'
import { Licensing } from './components/Licensing'
import { Overview } from './components/Overview'
import { Questionnaire } from './components/Questionnaire'
import { ManageAnnualReviewContext } from './context'

import { s } from './styles'

const routeApi = getRouteApi('/_app/annual-review/edit/$companyId/$id')

const AnnualReviewEditContainer = () => {
  const navigate = routeApi.useNavigate()
  const { id, companyId } = routeApi.useParams()
  const loaderData: AnnualReviewEditMode = routeApi.useLoaderData()

  const { mutate } = useMutation<void, Error, AnnualReviewEditMode['form']>({
    mutationFn: (opts) => Request.put(Request.urls.bank.annualReview, opts),
    onSuccess: async () => {
      navigate({ to: '/annual-review/detail/$companyId/$id', params: { companyId, id } })
      Toast.successSmall('Annual Review was successfully updated.')
    },
    onError: async (error) => handleFormSubmissionError<AnnualReviewEditMode['form']>(error, formApi)
  })

  const formApi = useForm({
    defaultValues: loaderData.form,
    onSubmit: async ({ value }) => await mutate(value)
  })

  return (
    <LeaveConfirmation preventLeaving={useStore(formApi.store, (s) => s.isDirty && !s.isSubmitting && !s.isSubmitted)}>
      <ManageAnnualReviewContext.Provider value={{ formApi }}>
        <Page
          face={PageFace.SECONDARY}
          title={loaderData.title}
          subTitle={`AR-${id}`}
          isPending={!id}
          footer={<Footer />}
        >
          <Layer rounded shadowed>
            <Panel collapsible={false} title={loaderData.title}>
              <s.FormPaddingWrapper>
                <Overview />
                <Hr key={1} />
                <Licensing />
                <Questionnaire />
              </s.FormPaddingWrapper>
            </Panel>
          </Layer>
        </Page>
      </ManageAnnualReviewContext.Provider>
    </LeaveConfirmation>
  )
}

export default AnnualReviewEditContainer
